import type { IAccordionItem } from "~/aihd-base-nuxt/types/components/accordion.type";
import type {
    IAboutService,
    IExploreItems,
    IHeroSubItem,
    IImageSlider,
    ILandingCardGroup,
    IVSHero,
} from "~/types/components/landings.type";

export const useVirtualStagingData = async () => {
    const { t } = useI18n();
    const auth = useAuth();
    const { isDesktop } = useDevice();

    const heroData: IVSHero = {
        title: t("pages_VS_hero_title"),
        title_span: t("pages_VS_hero_orange_title"),
        tag: t("pages_VS_hero_tag"),
        features: [
            {
                icon: "sofa",
                text: t("pages_VS_hero_feature_1"),
            },
            {
                icon: "infinity",
                text: t("pages_VS_hero_feature_2"),
            },
            {
                icon: "eraser",
                text: t("pages_VS_hero_feature_3"),
            },
            {
                icon: "currency-dollar",
                text: t("pages_VS_hero_feature_4"),
            },
        ],
        cta: {
            text: t("pages_VS_hero_cta_text"),
            link: "/dashboard/upload-photo/service-ai-virtual-staging",
            id: "virtualstaging-herosection-tryforfree-button",
        },
        slides: [
            {
                avatar_src: "/Landings/vs/Hero/herovs_pic_img1.webp",
                logo_src: "/Landings/vs/Hero/herovs_logo_img1.webp",
                image_src: "/Landings/vs/Hero/herovs-img1.webp",
                alt: "virtual staging example",
                title: "Jersenia Patterson",
                subtitle: t("pages_VS_hero_slide_1_subtitle"),
                description: t("pages_VS_hero_slide_1_description"),
                last_sentence: t("pages_VS_hero_slide_1_last_sentence"),
            },
            {
                avatar_src: "/Landings/vs/Hero/herovs_pic_img2.webp",
                logo_src: "/Landings/vs/Hero/herovs_logo_img2.webp",
                image_src: "/Landings/vs/Hero/herovs-img2.webp",
                alt: "real estate virtual staging",
                title: "Phil Radtke",
                subtitle: t("pages_VS_hero_slide_2_subtitle"),
                description: t("pages_VS_hero_slide_2_description"),
                last_sentence: t("pages_VS_hero_slide_2_last_sentence"),
            },
            {
                avatar_src: "/Landings/vs/Hero/herovs_pic_img3.webp",
                logo_src: "/Landings/vs/Hero/herovs_logo_img3.webp",
                image_src: "/Landings/vs/Hero/herovs-img3.webp",
                alt: "virtual staging before and after",
                title: "Brigitte Elzarki",
                subtitle: t("pages_VS_hero_slide_3_subtitle"),
                description: t("pages_VS_hero_slide_3_description"),
            },
            {
                avatar_src: "/Landings/vs/Hero/herovs_pic_img4.webp",
                logo_src: "/Landings/vs/Hero/herovs_logo_img4.webp",
                image_src: "/Landings/vs/Hero/herovs-img4.webp",
                alt: "ai virtual staging  example",
                title: "Steven Barkho",
                subtitle: t("pages_VS_hero_slide_4_subtitle"),
                description: t("pages_VS_hero_slide_4_description"),
                last_sentence: t("pages_VS_hero_slide_4_last_sentence"),
            },
        ],
    };

    const compareSlider: IImageSlider = {
        title: t("pages_VS_in_action_title"),
        slides: [
            {
                after: {
                    src: `https://revi.rvstatic.com/aihomedesign/images-v3/Landings/vs/vs-1-a${
                        isDesktop ? "" : "-s"
                    }.webp`,
                    alt: "after ai virtual staging",
                },
                before: {
                    src: `https://revi.rvstatic.com/aihomedesign/images-v3/Landings/vs/vs-1-b${
                        isDesktop ? "" : "-s"
                    }.webp`,
                    alt: "before ai virtual staging",
                },
            },
            {
                after: {
                    src: `https://revi.rvstatic.com/aihomedesign/images-v3/Landings/vs/vs-2-a${
                        isDesktop ? "" : "-s"
                    }.webp`,
                    alt: "after virtual staging with ai",
                },
                before: {
                    src: `https://revi.rvstatic.com/aihomedesign/images-v3/Landings/vs/vs-2-b${
                        isDesktop ? "" : "-s"
                    }.webp`,
                    alt: "before virtual staging with ai",
                },
            },
            {
                after: {
                    src: `https://revi.rvstatic.com/aihomedesign/images-v3/Landings/vs/vs-3-a${
                        isDesktop ? "" : "-s"
                    }.webp`,
                    alt: "after virtual staging service",
                },
                before: {
                    src: `https://revi.rvstatic.com/aihomedesign/images-v3/Landings/vs/vs-3-b${
                        isDesktop ? "" : "-s"
                    }.webp`,
                    alt: "before virtual staging service",
                },
            },
            {
                after: {
                    src: `https://revi.rvstatic.com/aihomedesign/images-v3/Landings/vs/vs-4-a${
                        isDesktop ? "" : "-s"
                    }.webp`,
                    alt: "after real estate virtual staging",
                },
                before: {
                    src: `https://revi.rvstatic.com/aihomedesign/images-v3/Landings/vs/vs-4-b${
                        isDesktop ? "" : "-s"
                    }.webp`,
                    alt: "before real estate virtual staging",
                },
            },
            {
                after: {
                    src: `https://revi.rvstatic.com/aihomedesign/images-v3/Landings/vs/vs-5-a${
                        isDesktop ? "" : "-s"
                    }.webp`,
                    alt: "after virtual staging",
                },
                before: {
                    src: `https://revi.rvstatic.com/aihomedesign/images-v3/Landings/vs/vs-5-b${
                        isDesktop ? "" : "-s"
                    }.webp`,
                    alt: "before virtual staging",
                },
            },
        ],
    };

    const imageSlider: IImageSlider = {
        title: t("global_serviceName_virtualStaging"),
        subtitle: t("global_serviceName_virtualStaging_desc"),
        slides: [
            {
                after: {
                    src: "/Virtual-Staging/Services/AI-Virtual-Staging/Large/1-A.webp",
                },
                before: {
                    src: "/Virtual-Staging/Services/AI-Virtual-Staging/Large/1.webp",
                },
            },
            {
                after: {
                    src: "/Virtual-Staging/Services/AI-Virtual-Staging/Large/2-A.webp",
                },
                before: {
                    src: "/Virtual-Staging/Services/AI-Virtual-Staging/Large/2.webp",
                },
            },
            {
                after: {
                    src: "/Virtual-Staging/Services/AI-Virtual-Staging/Large/3-A.webp",
                },
                before: {
                    src: "/Virtual-Staging/Services/AI-Virtual-Staging/Large/3.webp",
                },
            },
        ],

        cta: [
            {
                link: "/dashboard/upload-photo/service-ai-virtual-staging",
                text: t(auth.value ? "global_letsGo" : "global_tryForFree"),
                id: "virtualstaging-aivirtualstagingsection-tryforfree-button",
                type: "real-estate-hub",
            },
        ],
    };

    const vsClutteredRoomSlides: IImageSlider = {
        title: t("global_serviceName_virtualReStaging"),
        subtitle: t("global_serviceName_virtualReStaging_desc"),
        slides: [
            {
                after: {
                    src: "/Virtual-Staging/Services/AI-Virtual-Restaging/Large/1-A.webp",
                },
                before: {
                    src: "/Virtual-Staging/Services/AI-Virtual-Restaging/Large/1.webp",
                },
            },
            {
                after: {
                    src: "/Virtual-Staging/Services/AI-Virtual-Restaging/Large/2-A.webp",
                },
                before: {
                    src: "/Virtual-Staging/Services/AI-Virtual-Restaging/Large/2.webp",
                },
            },
            {
                after: {
                    src: "/Virtual-Staging/Services/AI-Virtual-Restaging/Large/3-A.webp",
                },
                before: {
                    src: "/Virtual-Staging/Services/AI-Virtual-Restaging/Large/3.webp",
                },
            },
        ],

        cta: [
            {
                link: "",
                text: t(auth.value ? "global_letsGo" : "global_tryForFree"),
                id: "virtualstaging-aivirtualrestagingsection-tryforfree-button",
                disabled: true,
                type: "real-estate-hub",
            },
        ],
    };

    const aboutItems: IExploreItems[] = [
        {
            text: t("global_serviceName_imageEnhancement"),
            link: "/image-enhancement",
            image: {
                src: "https://revi.rvstatic.com/aihomedesign/images-v3/Landings/general/tools-IE.webp",
                alt: "ai image enhancement",
            },
            id: "virtualstaging-othertoolsection-iecard",
        },
        {
            text: t("global_serviceName_itemRemoval"),
            link: "/item-removal",
            image: {
                src: "https://revi.rvstatic.com/aihomedesign/images-v3/Landings/general/tools-IR.webp",
                alt: "ai item removal",
            },
            id: "virtualstaging-othertoolsection-ircard",
        },
        {
            text: t("global_serviceName_dayToDusk"),
            link: "/day-to-dusk",
            image: {
                src: "https://revi.rvstatic.com/aihomedesign/images-v3/Landings/general/tools-DD.webp",
                alt: "ai day to dusk",
            },
            id: "virtualstaging-othertoolsection-ddcard",
        },
    ];

    const personalSpacesSection = {
        title: t("pages_VS_stand_out_title"),
        description: t("pages_VS_stand_out_desc"),
        items: [
            {
                title: t("pages_VS_stand_out_card_1_title"),
                subtitle: t("pages_VS_stand_out_card_1_desc"),
            },
            {
                title: t("pages_VS_stand_out_card_2_title"),
                subtitle: t("pages_VS_stand_out_card_2_desc"),
            },
            {
                title: t("pages_VS_stand_out_card_3_title"),
                subtitle: t("pages_VS_stand_out_card_3_desc"),
            },
        ],
        mobile_src: "https://revi.rvstatic.com/aihomedesign/images-v3/Landings/vs/vs-landing-s.webp",
        alt: "virtual staging for listing",
        desktop_src: "https://revi.rvstatic.com/aihomedesign/images-v3/Landings/vs/vs-landing.webp",
    };

    const faqFirstRow: IAccordionItem[] = [
        {
            title: t("pages_VS_faq_q1"),
            content: t("pages_VS_faq_a1"),
        },
        {
            title: t("pages_VS_faq_q2"),
            content: t("pages_VS_faq_a2"),
        },
        {
            title: t("pages_VS_faq_q3"),
            content: t("pages_VS_faq_a3"),
        },
        {
            title: t("pages_VS_faq_q4"),
            content: t("pages_VS_faq_a4"),
        },
        {
            title: t("pages_VS_faq_q5"),
            content: t("pages_VS_faq_a5"),
        },
    ];

    const faqSecondRow: IAccordionItem[] = [
        {
            title: t("pages_VS_faq_q6"),
            content: t("pages_VS_faq_a6"),
        },
        {
            title: t("pages_VS_faq_q7"),
            content: t("pages_VS_faq_a7"),
        },
        {
            title: t("pages_VS_faq_q8"),
            content: t("pages_VS_faq_a8"),
        },
        {
            title: t("pages_VS_faq_q9"),
            content: t("pages_VS_faq_a9"),
        },
        {
            title: t("pages_VS_faq_q10"),
            content: t("pages_VS_faq_a10"),
        },
    ];

    const aboutService: IAboutService = {
        title: t("pages_VS_about_title"),
        description: useDevice().isDesktop
            ? [t("pages_VS_about_desc_1"), t("pages_VS_about_desc_2")]
            : [t("pages_VS_about_desc_1")],
        moreDescription: useDevice().isDesktop ? [] : [t("pages_VS_about_desc_2")],
    };

    const heroSubItems: IHeroSubItem[] = [
        {
            text: t("pages_VS_hero_subService_IR"),
            link: "/item-removal",
            icon: "https://revi.rvstatic.com/aihomedesign/header-icons/IR.svg",
            id: "virtualstaging-itemremoval-herosection",
            image: {
                src: "/Virtual-Staging/Hero/Large/Hero-IR.png",
            },
        },
        {
            text: t("pages_VS_hero_subService_IE"),
            link: "/image-enhancement",
            icon: "https://revi.rvstatic.com/aihomedesign/header-icons/IE.svg",
            id: "virtualstaging-imageenhancement-herosection",
            image: {
                src: "/Virtual-Staging/Hero/Large/Hero-IE.png",
            },
        },
        {
            text: t("pages_VS_hero_subService_DD"),
            link: "/day-to-dusk",
            icon: "https://revi.rvstatic.com/aihomedesign/header-icons/D2D.svg",
            id: "virtualstaging-daytodusk-herosection",
            image: {
                src: "/Virtual-Staging/Hero/Large/Hero-D2D.png",
            },
        },
    ];

    const cardGroupItems: ILandingCardGroup = {
        title: t("pages_VS_cards_section_title"),
        items: [
            {
                img: { src: "/Landings/vs/01-VS.webp" },
                alt: "ai virtual staging step 1",
                title: t("pages_VS_card_1_title"),
                subtitle: t("pages_VS_card_1_subtitle"),
                icon: "tabler:eraser",
            },
            {
                img: { src: "/Landings/vs/02-VS.webp" },
                alt: "ai virtual staging step 2",
                title: t("pages_VS_card_2_title"),
                subtitle: t("pages_VS_card_2_subtitle"),
                icon: "tabler:color-swatch",
            },
            {
                img: { src: "/Landings/vs/03-VS.webp" },
                alt: "ai virtual staging step 3",
                title: t("pages_VS_card_3_title"),
                subtitle: t("pages_VS_card_3_subtitle"),
                icon: "tabler:edit",
            },
        ],
    };
    const purpleCta = {
        title: t("pages_VS_purple_cta_title"),
    };

    return {
        aboutItems,
        heroData,
        imageSlider,
        faqFirstRow,
        heroSubItems,
        aboutService,
        faqSecondRow,
        cardGroupItems,
        personalSpacesSection,
        vsClutteredRoomSlides,
        compareSlider,
        purpleCta,
    };
};
